import { Transaction } from '../types';

// Interface for joined transaction data with grading order information
export interface JoinedTransactionData extends Transaction {
  // Grading order fields
  grading_company?: string;
  grading_service_level?: string;
  grading_submission_date?: string;
  grading_return_date?: string;
  grading_number_of_cards?: number;
  grading_cost_per_card?: number;
  grading_total_gems?: number;
  grading_gem_rate?: number;
  grading_turnaround_time?: number;
}

// Interface for grading order data (minimal schema - user input only)
export interface GradingOrder {
  id?: string;
  order_id: string;
  company?: string;
  grading_service_level?: string;
  submission_date: string;
  cost?: number;
  return_date?: string;
  notes?: string;
  
  // System fields
  created_at?: string;
  updated_at?: string;
  
  // Calculated fields (added by CalculationService - not stored in DB)
  number_of_cards?: number;
  cost_per_card?: number;
  total_gems?: number;
  gem_rate?: number;
  turnaround_time?: number;
}

/**
 * Service for joining transaction and grading order data
 */
export class DataJoinService {
  
  /**
   * Join transactions with grading orders based on grading_order_id
   */
  static joinTransactionsWithGradingOrders(
    transactions: Transaction[],
    gradingOrders: GradingOrder[]
  ): JoinedTransactionData[] {
    
    // Create a lookup map for grading orders by order_id
    const gradingOrderMap = new Map<string, GradingOrder>();
    gradingOrders.forEach(order => {
      gradingOrderMap.set(order.order_id, order);
    });
    
    // Join transactions with grading orders
    return transactions.map(transaction => {
      const joinedData: JoinedTransactionData = { ...transaction };
      
      // If transaction has a grading_order_id, look up the grading order
      if (transaction.grading_order_id) {
        const gradingOrder = gradingOrderMap.get(transaction.grading_order_id);
        
        if (gradingOrder) {
          // Add grading order fields to the joined data
          joinedData.grading_company = gradingOrder.company;
          joinedData.grading_service_level = gradingOrder.grading_service_level;
          joinedData.grading_submission_date = gradingOrder.submission_date;
          joinedData.grading_return_date = gradingOrder.return_date;
          joinedData.grading_number_of_cards = gradingOrder.number_of_cards;
          // Note: cost_per_card, total_gems, gem_rate, and turnaround_time are now calculated by CalculationService
          // and will be added when the data is enriched
        }
      }
      
      return joinedData;
    });
  }
  
  /**
   * Get available dimensions for joined data
   */
  static getAvailableDimensions(joinedData: JoinedTransactionData[]): string[] {
    const dimensions = new Set<string>();
    
    // Standard transaction dimensions
    const transactionDimensions = [
      'card', 'sport', 'purchase_platform', 'sale_platform', 
      'grading_order_id', 'grade', 'notes'
    ];
    
    // Add transaction dimensions
    transactionDimensions.forEach(dim => dimensions.add(dim));
    
    // Check for grading order dimensions in the data
    const hasGradingData = joinedData.some(item => 
      item.grading_company || item.grading_service_level
    );
    
    if (hasGradingData) {
      // Add grading order dimensions
      const gradingDimensions = [
        'grading_company', 'grading_service_level'
      ];
      gradingDimensions.forEach(dim => dimensions.add(dim));
    }
    
    return Array.from(dimensions).sort();
  }
  
  /**
   * Get available metrics for joined data
   */
  static getAvailableMetrics(joinedData: JoinedTransactionData[]): string[] {
    const metrics = new Set<string>();
    
    // Standard transaction metrics (minimal data model)
    const transactionMetrics = [
      'cost', 'sale_price', 'marketplace_fees', 'shipping_fees', 'total_fees',
      'calculated_net_profit', 'calculated_total_cost', 'grading_cost', 'roi',
      'count_purchased', 'count_sold', 'count_graded', 'total_gems', 'gem_rate'
    ];
    
    // Add transaction metrics
    transactionMetrics.forEach(metric => metrics.add(metric));
    
    // Check for grading order metrics in the data
    const hasGradingData = joinedData.some(item => 
      item.grading_company || item.grading_service_level
    );
    
    if (hasGradingData) {
      // Add grading order metrics (only turnaround_time is displayable)
      const gradingMetrics = [
        'turnaround_time'
      ];
      gradingMetrics.forEach(metric => metrics.add(metric));
    }
    
    return Array.from(metrics).sort();
  }
  
  /**
   * Check if data sources can be joined
   */
  static canJoinDataSources(
    transactions: Transaction[],
    gradingOrders: GradingOrder[]
  ): boolean {
    if (!transactions.length || !gradingOrders.length) {
      return false;
    }
    
    // Check if any transactions have grading_order_id
    const hasGradingOrderIds = transactions.some(t => t.grading_order_id);
    
    // Check if any grading orders exist
    const hasGradingOrders = gradingOrders.length > 0;
    
    return hasGradingOrderIds && hasGradingOrders;
  }
  
  /**
   * Get join statistics
   */
  static getJoinStatistics(
    transactions: Transaction[],
    gradingOrders: GradingOrder[],
    joinedData: JoinedTransactionData[]
  ): {
    totalTransactions: number;
    transactionsWithGradingOrderId: number;
    totalGradingOrders: number;
    successfullyJoined: number;
    joinRate: number;
  } {
    const transactionsWithGradingOrderId = transactions.filter(t => t.grading_order_id).length;
    const successfullyJoined = joinedData.filter(j => j.grading_company).length;
    const joinRate = transactionsWithGradingOrderId > 0 
      ? (successfullyJoined / transactionsWithGradingOrderId) * 100 
      : 0;
    
    return {
      totalTransactions: transactions.length,
      transactionsWithGradingOrderId,
      totalGradingOrders: gradingOrders.length,
      successfullyJoined,
      joinRate: Math.round(joinRate * 100) / 100
    };
  }
}
